import { type ReactElement } from 'react';
import { useSession } from '../../auth';
import './PendingDeletionScreen.css';

/** The address the closed screen names, tappable (2026-10-21 Riley). */
const CONTACT_EMAIL = 'contact@oriscen.ai';

export type AccountClosedWhere = 'app_open' | 'sign_in' | 'in_session';

/**
 * What an account Oriscen closed sees (2026-10-20 founder; edge
 * account-deletion/account-stopped.js) — the same screen on the web and both
 * apps (2026-10-21 Riley): the title, one line with the address to write to,
 * and Log out. Closed is not deleted: nothing here counts down, and there is no
 * way back from the person's side, so no Restore.
 *
 * Three ways in, named by `where` (ui.seen account_closed_screen state):
 *   app_open   — /api/me said so when the app opened (EmberApp, in place of the router);
 *   in_session — a request of the open app was answered 403 account_closed: whatever
 *                was on screen (a chat, a call, a room) is unmounted under it;
 *   sign_in    — a sign-in door answered account_closed (/auth?account=closed).
 *
 * Log out forgets everything this browser kept for the account — cached
 * session, drafts, caches — and goes to the sign-in page.
 */
export function AccountClosedScreen({ where }: { where: AccountClosedWhere }): ReactElement {
  const { logoutClosed } = useSession();

  const signOut = async (): Promise<void> => {
    await logoutClosed();
    window.location.href = '/auth';
  };

  return (
    <main
      className="ember-pending-deletion"
      role="main"
      data-log-seen="account_closed_screen"
      data-log-state={where}
    >
      <div className="ember-pending-deletion-card">
        <h1>Your Ember account is closed</h1>
        <p>
          Ember won&apos;t reply to messages here anymore. If you have questions, email{' '}
          <a className="ember-account-closed-email" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
        <div className="ember-pending-deletion-actions">
          <button
            type="button"
            className="ember-btn ember-btn-secondary"
            data-log="account_closed_logout"
            onClick={() => { void signOut(); }}
          >
            Log out
          </button>
        </div>
      </div>
    </main>
  );
}
